// src/pages/ProgramList.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, Dialog, Snackbar, Alert, Card, CardContent, IconButton, useTheme
} from '@mui/material';
import {
    ArrowBack, Add, Edit, Delete, DragIndicator, Save, Visibility
} from '@mui/icons-material';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
    arrayMove, SortableContext, verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AdminHeader from '../components/AdminHeader';
import ProgramForm from '../components/ProgramForm';
import programAPI from '../services/programApi';
import { dayAPI } from '../services/api';
import { useAuth } from '../context/AuthContext'; // ✅ DODADENO

const SortableProgram = ({ program, onEdit, onDelete, onView }) => {
    const theme = useTheme();
    const {
        attributes, listeners, setNodeRef, transform, transition,
    } = useSortable({ id: program.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const formatDuration = (durationDays) => {
        if (!durationDays) return 'Неодредено';
        if (typeof durationDays === 'number') return `${durationDays} денови`;
        if (typeof durationDays === 'string') {
            if (durationDays.includes('денови') || durationDays.includes('месеци') || durationDays.includes('недели')) {
                return durationDays;
            }
            return `${durationDays} денови`;
        }
        return 'Неодредено';
    };

    return (
        <Card
            ref={setNodeRef}
            style={style}
            sx={{
                mb: 2,
                border: '2px solid #ff7eb9',
                borderRadius: '16px',
                backgroundColor: theme.palette.background.paper,
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(255, 126, 185, 0.3)',
                    borderColor: '#ff4a97'
                }
            }}
        >
            <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {/* Drag Handle */}
                    <IconButton
                        {...attributes}
                        {...listeners}
                        sx={{
                            color: '#ff7eb9', cursor: 'grab', mt: 0.5,
                            '&:active': { cursor: 'grabbing' },
                            '&:hover': { backgroundColor: 'rgba(255, 126, 185, 0.1)' }
                        }}
                    >
                        <DragIndicator />
                    </IconButton>

                    {/* Program Info */}
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" sx={{
                            color: '#ff7eb9', fontWeight: 'bold', mb: 1,
                            textShadow: '0 2px 4px rgba(255, 126, 185, 0.2)'
                        }}>
                            🏋️‍♂️ {program.name}
                        </Typography>

                        <Typography variant="body1" sx={{
                            color: theme.palette.text.secondary, mb: 2, lineHeight: 1.6
                        }}>
                            {program.description || 'Нема опис'}
                        </Typography>

                        {/* Stats */}
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                            <Box sx={{
                                display: 'flex', alignItems: 'center',
                                backgroundColor: 'rgba(165, 216, 255, 0.1)',
                                px: 2, py: 1, borderRadius: '8px',
                                border: '1px solid rgba(165, 216, 255, 0.3)'
                            }}>
                                <Typography variant="body2" sx={{ color: '#a5d8ff', fontWeight: 'bold' }}>
                                    💰 Цена: {program.price ? `${program.price} ден.` : '0 ден.'}
                                </Typography>
                            </Box>

                            <Box sx={{
                                display: 'flex', alignItems: 'center',
                                backgroundColor: 'rgba(200, 240, 204, 0.1)',
                                px: 2, py: 1, borderRadius: '8px',
                                border: '1px solid rgba(200, 240, 204, 0.3)'
                            }}>
                                <Typography variant="body2" sx={{ color: '#c8f0cc', fontWeight: 'bold' }}>
                                    📅 Денови: {program.days?.length || 0}
                                </Typography>
                            </Box>

                            <Box sx={{
                                display: 'flex', alignItems: 'center',
                                backgroundColor: 'rgba(255, 220, 150, 0.1)',
                                px: 2, py: 1, borderRadius: '8px',
                                border: '1px solid rgba(255, 220, 150, 0.3)'
                            }}>
                                <Typography variant="body2" sx={{ color: '#ffdc96', fontWeight: 'bold' }}>
                                    ⏱️ Времетраење: {formatDuration(program.durationDays)}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Days List */}
                        {program.days && program.days.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" sx={{
                                    color: theme.palette.text.secondary, mb: 1, fontWeight: 'bold'
                                }}>
                                    Вклучени денови:
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {program.days.map(day => (
                                        <Box key={day.id} sx={{
                                            backgroundColor: 'rgba(255, 126, 185, 0.1)',
                                            color: '#ff7eb9', px: 1.5, py: 0.5,
                                            borderRadius: '12px', fontSize: '12px', fontWeight: 'bold',
                                            border: '1px solid rgba(255, 126, 185, 0.3)'
                                        }}>
                                            {day.title}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                        <IconButton onClick={(e) => { e.stopPropagation(); onView(program); }}
                                    sx={{ color: '#c8f0cc', transition: 'all 0.2s ease',
                                        '&:hover': { backgroundColor: 'rgba(200, 240, 204, 0.1)', transform: 'scale(1.1)' }
                                    }} title="Управувај со денови">
                            <Visibility />
                        </IconButton>
                        <IconButton onClick={(e) => { e.stopPropagation(); onEdit(program); }}
                                    sx={{ color: '#a5d8ff', transition: 'all 0.2s ease',
                                        '&:hover': { backgroundColor: 'rgba(165, 216, 255, 0.1)', transform: 'scale(1.1)' }
                                    }}>
                            <Edit />
                        </IconButton>
                        <IconButton onClick={(e) => { e.stopPropagation(); onDelete(program); }}
                                    sx={{ color: '#ff6b6b', transition: 'all 0.2s ease',
                                        '&:hover': { backgroundColor: 'rgba(255, 107, 107, 0.1)', transform: 'scale(1.1)' }
                                    }}>
                            <Delete />
                        </IconButton>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const ProgramList = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { user, loading: authLoading } = useAuth(); // ✅ DODADENO
    const [programs, setPrograms] = useState([]);
    const [days, setDays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addProgramDialog, setAddProgramDialog] = useState(false);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // ✅ PROMENETO: cekaj auth da е gotov pred da fetchiras
    useEffect(() => {
        if (!authLoading && user) {
            fetchData();
        }
    }, [user, authLoading]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [programsData, daysData] = await Promise.all([
                programAPI.admin.getAll(),
                dayAPI.admin.getAll()
            ]);
            setPrograms(programsData);
            setDays(daysData);
        } catch (err) {
            console.error('Error fetching data:', err);
            // ✅ PROMENETO: podobra error poraka
            if (err.response?.status === 403) {
                showSnackbar('Немате пристап. Обидете се да се одјавите и најавите повторно.', 'error');
            } else {
                showSnackbar(err.response?.data?.message || 'Грешка при вчитување на податоците', 'error');
            }
            setPrograms([]);
            setDays([]);
        } finally {
            setLoading(false);
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleViewProgram = (program) => {
        navigate(`/admin/programs/${program.id}/days`);
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = programs.findIndex(p => p.id === active.id);
            const newIndex = programs.findIndex(p => p.id === over.id);
            setPrograms(arrayMove(programs, oldIndex, newIndex));
            showSnackbar('Редоследот е променет. Зачувајте ги промените.');
        }
    };

    const handleSaveProgram = async (programData) => {
        try {
            if (programData.id) {
                const saved = await programAPI.admin.update(programData.id, programData);
                setPrograms(prev => prev.map(p => p.id === programData.id ? saved : p));
                showSnackbar('Програмата е успешно ажурирана 🎉');
            } else {
                const saved = await programAPI.admin.create(programData);
                setPrograms(prev => [...prev, saved]);
                showSnackbar('Програмата е успешно креирана 🎉');
            }
            setAddProgramDialog(false);
            setSelectedProgram(null);
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Грешка при зачувување на програмата', 'error');
        }
    };

    const handleDeleteProgram = async (program) => {
        if (window.confirm(`Дали сте сигурни дека сакате да ја избришете програмата "${program.name}"?\nОваа акција е неповратна!`)) {
            try {
                await programAPI.admin.delete(program.id);
                setPrograms(prev => prev.filter(p => p.id !== program.id));
                showSnackbar('Програмата е успешно избришана 🗑️');
            } catch (err) {
                showSnackbar('Грешка при бришење на програмата', 'error');
            }
        }
    };

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
                <Box sx={{ px: 4, pt: 4 }}><AdminHeader /></Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <Typography variant="h4" sx={{ color: '#ff7eb9', fontWeight: 'bold' }}>
                        <Box component="span" sx={{ animation: 'pulse 1.5s ease-in-out infinite', mr: 2 }}>⏳</Box>
                        Вчитување на програми...
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ py: 4, minHeight: '100vh', width: '100%', backgroundColor: theme.palette.background.default }}>
                <Box sx={{ px: 4 }}>
                    <AdminHeader />
                </Box>

                <Box sx={{ px: 4 }}>
                    {/* Header */}
                    <Box sx={{
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'flex-start', mb: 4, flexWrap: 'wrap', gap: 2
                    }}>
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Button
                                    onClick={() => navigate('/admin')}
                                    startIcon={<ArrowBack />}
                                    variant="contained"
                                    sx={{
                                        bgcolor: '#a5d8ff', color: '#2b2b2b', mr: 2,
                                        fontWeight: 'bold', borderRadius: '12px', px: 3, py: 1,
                                        boxShadow: '0 4px 15px rgba(165, 216, 255, 0.3)',
                                        '&:hover': {
                                            bgcolor: '#74c0fc', transform: 'translateY(-2px)',
                                            boxShadow: '0 6px 20px rgba(165, 216, 255, 0.4)',
                                        },
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    Назад кон админ панелот
                                </Button>
                                <Typography variant="h3" sx={{
                                    color: '#ff7eb9', fontWeight: 'bold',
                                    textShadow: '0 2px 4px rgba(255, 126, 185, 0.3)'
                                }}>
                                    🏋️‍♂️ Фитнес Програми
                                </Typography>
                            </Box>
                            <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
                                Управувајте со вашите фитнес програми, времетраење и денови
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Button variant="outlined" onClick={() => showSnackbar('Редоследот е зачуван! ✅')}
                                    startIcon={<Save />}
                                    sx={{
                                        color: '#a5d8ff', borderColor: '#a5d8ff',
                                        fontWeight: 'bold', px: 3, py: 1.5, borderRadius: '12px',
                                        '&:hover': {
                                            borderColor: '#74c0fc',
                                            backgroundColor: 'rgba(165, 216, 255, 0.1)',
                                            transform: 'translateY(-2px)'
                                        },
                                        transition: 'all 0.3s ease'
                                    }}>
                                Зачувај редослед
                            </Button>
                            <Button variant="contained"
                                    onClick={() => { setSelectedProgram(null); setAddProgramDialog(true); }}
                                    startIcon={<Add />}
                                    sx={{
                                        bgcolor: '#ff7eb9', color: '#fff', fontWeight: 'bold',
                                        px: 3, py: 1.5, borderRadius: '12px',
                                        boxShadow: '0 4px 15px rgba(255, 126, 185, 0.3)',
                                        '&:hover': {
                                            bgcolor: '#ff4a97', transform: 'translateY(-2px)',
                                            boxShadow: '0 6px 20px rgba(255, 126, 185, 0.4)',
                                        },
                                        transition: 'all 0.3s ease'
                                    }}>
                                Додај програма
                            </Button>
                        </Box>
                    </Box>

                    {/* Programs List */}
                    {programs.length === 0 ? (
                        <Box sx={{
                            textAlign: 'center', py: 12,
                            border: '2px dashed #ff7eb9', borderRadius: '16px',
                            backgroundColor: 'rgba(255, 126, 185, 0.05)'
                        }}>
                            <Typography variant="h4" sx={{ color: '#ff7eb9', mb: 2, fontWeight: 'bold' }}>
                                📋 Нема програми
                            </Typography>
                            <Typography variant="h6" sx={{
                                color: theme.palette.text.secondary, mb: 4,
                                maxWidth: '500px', mx: 'auto', lineHeight: 1.6
                            }}>
                                Креирајте ваша прва фитнес програма за да започнете со организирање на денови и вежби
                            </Typography>
                            <Button variant="contained"
                                    onClick={() => { setSelectedProgram(null); setAddProgramDialog(true); }}
                                    startIcon={<Add />}
                                    sx={{
                                        bgcolor: '#ff7eb9', color: '#fff', fontSize: '18px',
                                        px: 4, py: 2, borderRadius: '12px',
                                        '&:hover': { bgcolor: '#ff4a97', transform: 'translateY(-3px)' },
                                        transition: 'all 0.3s ease'
                                    }}>
                                Креирај прва програма
                            </Button>
                        </Box>
                    ) : (
                        <Box>
                            {/* Stats Header */}
                            <Box sx={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                mb: 3, p: 3,
                                backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 126, 185, 0.08)'
                                    : 'rgba(255, 126, 185, 0.05)',
                                borderRadius: '16px',
                                border: '1px solid rgba(255, 126, 185, 0.2)'
                            }}>
                                <Box>
                                    <Typography variant="h5" sx={{ color: '#ff7eb9', fontWeight: 'bold', mb: 1 }}>
                                        📊 Вкупно програми: {programs.length}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                        🎯 Вкупно денови: {programs.reduce((t, p) => t + (p.days?.length || 0), 0)}
                                    </Typography>
                                </Box>
                                <Typography variant="body1" sx={{
                                    color: theme.palette.text.secondary,
                                    backgroundColor: 'rgba(165, 216, 255, 0.1)',
                                    px: 2, py: 1, borderRadius: '8px',
                                    border: '1px solid rgba(165, 216, 255, 0.3)'
                                }}>
                                    👆 Влечете и спуштајте за да го промените редоследот
                                </Typography>
                            </Box>

                            {/* Drag & Drop */}
                            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                <SortableContext items={programs.map(p => p.id)} strategy={verticalListSortingStrategy}>
                                    {programs.map(program => (
                                        <SortableProgram
                                            key={program.id}
                                            program={program}
                                            onEdit={(p) => { setSelectedProgram(p); setAddProgramDialog(true); }}
                                            onDelete={handleDeleteProgram}
                                            onView={handleViewProgram}
                                        />
                                    ))}
                                </SortableContext>
                            </DndContext>

                            {/* Stats Footer */}
                            <Box sx={{
                                mt: 4, p: 3, textAlign: 'center',
                                backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(165, 216, 255, 0.08)'
                                    : 'rgba(165, 216, 255, 0.05)',
                                borderRadius: '16px',
                                border: '1px solid rgba(165, 216, 255, 0.2)'
                            }}>
                                <Typography variant="h6" sx={{ color: '#a5d8ff', mb: 2, fontWeight: 'bold' }}>
                                    📈 Статистика за програми
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
                                    {[
                                        `🏋️‍♂️ Вкупно програми: ${programs.length}`,
                                        `📅 Вкупно денови: ${programs.reduce((t, p) => t + (p.days?.length || 0), 0)}`,
                                        `📊 Просечно: ${(programs.reduce((t, p) => t + (p.days?.length || 0), 0) / programs.length).toFixed(1)} денови`
                                    ].map((text, i) => (
                                        <Typography key={i} variant="body2" sx={{
                                            color: theme.palette.text.secondary,
                                            backgroundColor: theme.palette.background.paper,
                                            px: 2, py: 1, borderRadius: '8px',
                                            border: '1px solid rgba(255, 126, 185, 0.2)'
                                        }}>
                                            {text}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>

                <Dialog open={addProgramDialog} onClose={() => { setAddProgramDialog(false); setSelectedProgram(null); }}
                        maxWidth="md" fullWidth
                        PaperProps={{ sx: { borderRadius: '16px', border: '2px solid #ff7eb9' } }}>
                    <ProgramForm
                        initialData={selectedProgram}
                        days={days}
                        onSave={handleSaveProgram}
                        onCancel={() => { setAddProgramDialog(false); setSelectedProgram(null); }}
                    />
                </Dialog>
            </Box>

            <Snackbar open={snackbar.open} autoHideDuration={4000}
                      onClose={() => setSnackbar({ ...snackbar, open: false })}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity}
                       onClose={() => setSnackbar({ ...snackbar, open: false })}
                       sx={{ borderRadius: '12px', fontWeight: 'bold' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <style>{`
                @keyframes pulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.1); }
                    100% { transform: scale(1); }
                }
            `}</style>
        </>
    );
};

export default ProgramList;